import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Organization } from 'src/models/organization';
import { FormikProps } from 'formik/dist/types';
import { Formik } from 'formik';
import { GenericMessages } from 'src/intl/GenericMessages';
import { FirmwareMessages } from 'src/intl/FirmwareMessages';
import AddFirmwareDetailsForm from './AddFirmwareDetailsForm';
import { FirmwareFileUploadForm } from './FirmwareFileUploadForm';
import { Firmware } from 'src/models/firmware';
import { FirmwareDetailsData, FirmwareFile } from './types';
import { AppModal } from '../reusable-components/app-modal/AppModal';
import { FirmwareTier } from '../../models/FirmwareTier';
import * as yup from 'yup';

interface CreateOrUpdateFirmwareModalProps {
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Triggers the request to close the modal.
   */
  onClose: () => void;
  /**
   * Triggers when a form is submitted.
   */
  onSave: (e: FirmwareDetailsData, firmwareFile?: FirmwareFile) => Promise<void>;
  /**
   * The Organization options for beta users.
   */
  organizations: Organization[];
  /**
   * The existing firmware values of a firmware being updated.
   */
  existingFirmwareValues?: Firmware;
  /**
   * The firmware tier options.
   */
  firmwareTiers: FirmwareTier[];
}

export const CreateOrUpdateFirmwareModal: React.FC<CreateOrUpdateFirmwareModalProps> = ({
  onClose,
  isOpen,
  onSave,
  organizations,
  existingFirmwareValues,
  firmwareTiers,
}) => {
  const { formatMessage } = useIntl();
  const [firmwareFile, setFirmwareFile] = useState<FirmwareFile | undefined>(undefined);

  const validationSchema = React.useMemo(() => {
    return yup.object({
      displayName: yup.string().required(formatMessage(FirmwareMessages.fieldIsRequired)),
      fwVersion: yup.string().required(formatMessage(FirmwareMessages.fieldIsRequired)),
      deploymentType: yup.string().required(formatMessage(FirmwareMessages.fieldIsRequired)),
      archiveFirmware: yup.string().required(formatMessage(FirmwareMessages.fieldIsRequired)),
      organization: yup.string().when('deploymentType', {
        is: (deploymentType: any) => deploymentType === 'custom',
        then: yup.string().required(formatMessage(FirmwareMessages.fieldIsRequired)),
        otherwise: yup.string().nullable(),
      }),
      firmwareTier: yup.string().when('deploymentType', {
        is: (deploymentType: any) => deploymentType === 'paid',
        then: yup.string().required(formatMessage(FirmwareMessages.fieldIsRequired)),
        otherwise: yup.string().nullable(),
      }),
    });
  }, [formatMessage]);

  const submitFirmwareDetailsForm = useCallback(
    async (formValues: FirmwareDetailsData) => {
      if (firmwareFile) {
        await onSave(formValues, firmwareFile);
      } else await onSave(formValues);
    },
    [firmwareFile, onSave],
  );

  const isEditMode = !!existingFirmwareValues;

  const users =
    existingFirmwareValues?.releaseType === 'beta' ? existingFirmwareValues.beta : existingFirmwareValues?.users;

  return (
    <Formik
      initialValues={{
        binFile: '',
        displayName: existingFirmwareValues?.displayName ? existingFirmwareValues.displayName : '',
        fwVersion: existingFirmwareValues?.version ? existingFirmwareValues.version : '',
        deploymentType: existingFirmwareValues?.releaseType ? existingFirmwareValues.releaseType : undefined,
        archiveFirmware: existingFirmwareValues?.isHidden ?? 'false',
        users: users ?? [],
        organizations: existingFirmwareValues?.orgs ?? [],
        organization: existingFirmwareValues?.custom ? existingFirmwareValues.custom : '',
        firmwareTier: existingFirmwareValues?.firmwareTierId,
      }}
      validationSchema={validationSchema}
      onSubmit={(values: FirmwareDetailsData) => submitFirmwareDetailsForm(values)} // TODO: Need to clear formik props properly on saving
      enableReinitialize
    >
      {(formikProps: FormikProps<FirmwareDetailsData>) => (
        <form onSubmit={formikProps.handleSubmit}>
          <AppModal
            isOpen={isOpen}
            title={formatMessage(isEditMode ? FirmwareMessages.editFirmware : FirmwareMessages.createFirmware)}
            onClose={() => {
              formikProps.resetForm();
              setFirmwareFile(undefined);
              onClose();
            }}
            primaryButton={{
              label: formatMessage(isEditMode ? FirmwareMessages.update : FirmwareMessages.upload),
              onClick: formikProps.handleSubmit,
            }}
            secondaryButton={{
              label: formatMessage(GenericMessages.cancelButtonLabel),
              onClick: () => {
                formikProps.resetForm();
                setFirmwareFile(undefined);
                onClose();
              },
            }}
          >
            {!firmwareFile && !isEditMode && (
              <FirmwareFileUploadForm
                onSave={uploadedFile => {
                  formikProps.values.binFile = uploadedFile.name;
                  setFirmwareFile(uploadedFile);
                }}
              />
            )}
            {(firmwareFile || isEditMode) && (
              <AddFirmwareDetailsForm
                organizations={organizations}
                firmwareTiers={firmwareTiers}
                formikProps={formikProps}
                update={!!existingFirmwareValues}
              />
            )}
          </AppModal>
        </form>
      )}
    </Formik>
  );
};
